import { useEffect, useMemo, useState } from 'react'
import { Link, useNavigate, useOutletContext } from 'react-router-dom'

import { listElements } from '../../../api/elements.js'
import { listGroupConfigs } from '../../../api/groupConfigs.js'
import { checkSchedulerDefault, createScheduler } from '../../../api/schedulers.js'
import { validateSchedulerForm } from './schedulerValidation.js'

const DATE_FORMAT_OPTIONS = ['timestamp', 'iso8601']

export default function SchedulerCreatePage() {
  const { applicationId } = useOutletContext()
  const navigate = useNavigate()

  const [state, setState] = useState({
    status: 'loading',
    groupConfigs: [],
    elements: [],
    error: null,
  })
  const [values, setValues] = useState({
    groupConfigId: '',
    elementId: '',
    name: '',
    startDate: '',
    endDate: '',
    outputFormatTime: 'timestamp',
    duration: '',
    isDefault: false,
    isFrozen: false,
  })
  const [forceDefault, setForceDefault] = useState(false)
  const [error, setError] = useState(null)
  const [isSubmitting, setIsSubmitting] = useState(false)

  useEffect(() => {
    if (!values.groupConfigId || !values.elementId) {
      setForceDefault(false)
      return
    }
    let cancelled = false
    checkSchedulerDefault(applicationId, values.groupConfigId, values.elementId)
      .then((result) => {
        if (cancelled) return
        if (!result.hasSchedulers) {
          setForceDefault(true)
          setValues((prev) => ({ ...prev, isDefault: true }))
        } else {
          setForceDefault(false)
        }
      })
      .catch(() => {
        if (!cancelled) setForceDefault(false)
      })
    return () => {
      cancelled = true
    }
  }, [applicationId, values.groupConfigId, values.elementId])

  useEffect(() => {
    let cancelled = false
    Promise.all([listGroupConfigs(applicationId), listElements(applicationId)])
      .then(([groupConfigs, elements]) => {
        if (cancelled) return
        setState({
          status: 'ok',
          groupConfigs,
          elements,
          error: null,
        })
      })
      .catch((loadError) => {
        if (cancelled) return
        setState({
          status: 'error',
          groupConfigs: [],
          elements: [],
          error: loadError,
        })
      })
    return () => {
      cancelled = true
    }
  }, [applicationId])

  const filteredElements = useMemo(() => {
    if (!values.groupConfigId) return state.elements
    return state.elements.filter((element) => String(element.groupConfigId || '') === String(values.groupConfigId))
  }, [state.elements, values.groupConfigId])

  function onChange(field) {
    return (event) => {
      const isCheckbox = field === 'isDefault' || field === 'isFrozen'
      const nextValue = isCheckbox ? event.target.checked : event.target.value
      setValues((prev) => {
        const next = { ...prev, [field]: nextValue }
        if (field === 'groupConfigId') {
          next.elementId = ''
        }
        return next
      })
    }
  }

  async function handleSubmit(event) {
    event.preventDefault()
    if (isSubmitting) return
    setError(null)

    const validation = validateSchedulerForm(values)
    if (!validation.success) {
      setError(new Error(validation.error))
      return
    }

    try {
      setIsSubmitting(true)
      const created = await createScheduler(applicationId, validation.data)
      if (created?.id) {
        navigate(`/applications/${applicationId}/scheduler/${created.id}`)
      }
    } catch (submitError) {
      const detail = submitError?.body?.detail
      setError(new Error(detail ? String(detail) : String(submitError?.message || submitError)))
    } finally {
      setIsSubmitting(false)
    }
  }

  if (state.status === 'loading') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Loading scheduler form...</div>
      </section>
    )
  }

  if (state.status === 'error') {
    return (
      <section className="card sectionCard">
        <div className="cardTitle">Failed to load scheduler form</div>
        <pre className="codeBlock codeBlockError">{String(state.error?.message || state.error)}</pre>
      </section>
    )
  }

  return (
    <div className="page">
      <div className="pageHeader">
        <h1 className="pageTitle">Create</h1>
        <Link to={`/applications/${applicationId}/scheduler`} className="button buttonGhost">
          Back to scheduler
        </Link>
      </div>

      <section className="card formCard">
        <div className="cardTitle">Scheduler</div>
        <form className="formGrid" onSubmit={handleSubmit}>
          <label className="formField">
            <span className="formLabel">Name</span>
            <input className="textInput" value={values.name} onChange={onChange('name')} placeholder="e.g. offer_start" />
          </label>

          <label className="formField">
            <span className="formLabel">Group config</span>
            <select className="textInput" value={String(values.groupConfigId || '')} onChange={onChange('groupConfigId')}>
              <option value="">Choose group config</option>
              {state.groupConfigs.map((groupConfig) => (
                <option key={groupConfig.id} value={String(groupConfig.id)}>
                  {groupConfig.name || `Group #${groupConfig.id}`}
                </option>
              ))}
            </select>
          </label>

          <label className="formField">
            <span className="formLabel">Element</span>
            <select
              className="textInput"
              value={String(values.elementId || '')}
              onChange={onChange('elementId')}
              disabled={!values.groupConfigId}
            >
              <option value="">{values.groupConfigId ? 'Choose element' : 'Choose group config first'}</option>
              {filteredElements.map((element) => (
                <option key={element.id} value={String(element.id)}>
                  {element.name || `Element #${element.id}`} {element.version ? `v${element.version}` : ''}
                </option>
              ))}
            </select>
            {values.groupConfigId && filteredElements.length === 0 ? (
              <span className="formHint">No elements linked to this group config.</span>
            ) : null}
          </label>

          <label className="formField">
            <span className="formLabel">Start date (cron)</span>
            <input
              className="textInput mono"
              value={values.startDate}
              onChange={onChange('startDate')}
              placeholder="0 0 * * *"
            />
          </label>

          <label className="formField">
            <span className="formLabel">End date (cron)</span>
            <input
              className="textInput mono"
              value={values.endDate}
              onChange={onChange('endDate')}
              placeholder="Optional"
            />
          </label>

          <label className="formField">
            <span className="formLabel">Duration</span>
            <input
              className="textInput"
              type="number"
              min="0"
              value={values.duration}
              onChange={onChange('duration')}
              placeholder="Optional"
            />
          </label>

          <label className="formField">
            <span className="formLabel">Output format</span>
            <select className="textInput" value={values.outputFormatTime} onChange={onChange('outputFormatTime')}>
              {DATE_FORMAT_OPTIONS.map((format) => (
                <option key={format} value={format}>
                  {format}
                </option>
              ))}
            </select>
          </label>

          <label className="formField formFieldInline">
            <input
              type="checkbox"
              checked={values.isDefault}
              onChange={onChange('isDefault')}
              disabled={forceDefault}
            />
            <span className="formLabel formLabelNoMargin">
              Is default
            </span>
            {forceDefault ? (
              <span className="formHint">First scheduler for this element must be default.</span>
            ) : null}
          </label>

          <label className="formField formFieldInline">
            <input type="checkbox" checked={values.isFrozen} onChange={onChange('isFrozen')} />
            <span className="formLabel formLabelNoMargin">
              Is frozen
            </span>
          </label>

          <div className="formActions">
            <button type="submit" className="button buttonPrimary" disabled={isSubmitting}>
              {isSubmitting ? 'Creating...' : 'Create'}
            </button>
          </div>
        </form>

        {error ? <div className="formError">{String(error?.message || error)}</div> : null}
      </section>
    </div>
  )
}
